import { z } from 'zod';

/**
 * The environment, validated once at startup.
 *
 * <h2>Why this is not just `import.meta.env` at each use site</h2>
 *
 * Vite substitutes `import.meta.env.VITE_*` at build time and types every one of
 * them as `string`, whether or not it was set. A missing variable is therefore
 * `undefined` wearing a `string` type, and it surfaces as `undefined` appearing
 * in a URL somewhere far from the cause.
 *
 * Parsed here, a misconfigured build fails on load with a message naming the
 * variable — which is the only moment anyone is in a position to fix it.
 */
const schema = z.object({
  /**
   * Empty in development, on purpose: the dev server proxies `/api` and
   * `/oauth2`, so the browser stays on one origin and requests are relative.
   * Set it only to point at a backend on another host.
   */
  VITE_API_BASE_URL: z.string().default(''),

  /**
   * The public OAuth client this browser identifies as.
   *
   * <p>Defaulted rather than required. It authenticates with `none` and holds no
   * secret — that is why the backend demands PKCE on its authorization-code flow
   * — and the value is the same in every environment, so demanding a file that
   * contains only a known constant is friction that buys nothing. A fresh clone
   * runs with no `.env.local` at all.
   *
   * <p>Override it when a deployment registers a different client.
   */
  VITE_OAUTH_CLIENT_ID: z.string().min(1).default('Xtremand-React-Client'),

  /**
   * The Google Sheet a Gmail receiver copies to join the pool. A public copy link, not a secret,
   * and different in each environment. Empty means the operator's "Create Receiver" screen says
   * it is not set up.
   */
  VITE_WARMUP_RECEIVER_SHEET_URL: z.string().default(''),

  /**
   * Where a Microsoft receiver signs in to join the pool. Empty means this application's own
   * `/api/v1/email-warmup/microsoft/login`, which is what almost every deployment wants.
   */
  VITE_WARMUP_MICROSOFT_RECEIVER_LOGIN_URL: z.string().default(''),
});

const parsed = schema.safeParse(import.meta.env);

if (!parsed.success) {
  const missing = parsed.error.issues.map((issue) => issue.path.join('.')).join(', ');
  throw new Error(
    `Environment is not configured: ${missing}. Copy .env.example to .env.local and fill it in.`,
  );
}

export const env = {
  apiBaseUrl: parsed.data.VITE_API_BASE_URL,
  oauthClientId: parsed.data.VITE_OAUTH_CLIENT_ID,
  warmupReceiverSheetUrl: parsed.data.VITE_WARMUP_RECEIVER_SHEET_URL,
  warmupMicrosoftReceiverLoginUrl: parsed.data.VITE_WARMUP_MICROSOFT_RECEIVER_LOGIN_URL,
  isDevelopment: import.meta.env.DEV,
} as const;
